import { useEffect, useState } from 'react';
import { Button, Box, TextField, Typography } from '@mui/material';
import prettyMs from 'pretty-ms';
import prettyBytes from 'pretty-bytes';
import { LoadingButton } from '@mui/lab';
import {
  getExports,
  downloadExports,
  buildExports,
} from '../../services/admin-service';
import { useAlerts } from '../../hooks/use-alerts';
import { Table } from '../../components/table/Table';
import { Dialog } from '../../components/Dialog';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';

export function GrowerExport() {
  const [exports, setExports] = useState();
  const [selected, setSelected] = useState([]);
  const [isDownloading, setDownloading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState();
  const [buildExport, setBuildExport] = useState();
  const { showInfo, showError } = useAlerts();

  useEffect(() => {
    getExports().then(setExports).catch(showError);
  }, [showError]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        pt: 0,
        height: '100%',
      }}
    >
      {showDownloadLink()}
      {showBuildExport()}
      <Table
        hideFooter={false}
        loading={!exports}
        columns={buildColumns()}
        rows={exports || []}
        checkboxSelection
        rowSelectionModel={selected}
        onRowSelectionModelChange={setSelected}
        actions={[
          <Button key="build" onClick={() => setBuildExport(true)}>
            Build Exports
          </Button>,
          <LoadingButton
            key="download"
            onClick={downloadSelected}
            disabled={!selected.length}
            loading={isDownloading}
            keep-open="true"
          >
            Download
          </LoadingButton>,
        ]}
      />
    </Box>
  );

  function showDownloadLink() {
    if (!downloadUrl) return null;

    return (
      <Dialog
        title="Export"
        onCancel={() => setDownloadUrl(false)}
        actions={false}
      >
        <Typography>
          {'Download the zip '}
          <a href={downloadUrl}>here</a>
          {'. It will expire in one day.'}
        </Typography>
      </Dialog>
    );
  }

  function showBuildExport() {
    if (!buildExport) return null;

    return (
      <ConfirmationDialog
        titleIcon={false}
        title="Build exports"
        cancelButtonText="Cancel"
        continueButtonText="Build"
        handleClose={() => setBuildExport(null)}
        handleConfirm={async (data) => {
          await buildExports(data);
          setBuildExport(false);
          showInfo('Export jobs scheduled');
        }}
        renderBody={(setConfirmPayload) => (
          <TextField
            sx={{ mt: 1, width: '100%' }}
            label="Grower Ids (comma separated accepted)"
            multiline
            rows={6}
            variant="outlined"
            onChange={(event) => setConfirmPayload(event.target.value)}
          />
        )}
      />
    );
  }

  function downloadSelected() {
    setDownloading(true);
    downloadExports(selected)
      .then(setDownloadUrl)
      .catch(showError)
      .finally(() => setDownloading(false));
  }

  function buildColumns() {
    return [
      {
        field: 'growerName',
        headerName: 'Grower',
        flex: 1.5,
      },
      {
        field: 'startedAt',
        type: 'dateTime',
        headerName: 'Started',
        valueFormatter: ({ value }) => new Date(value).toLocaleString(),
        flex: 1,
      },
      {
        field: 'completedAt',
        type: 'dateTime',
        headerName: 'Ended',
        valueFormatter: ({ value }) => new Date(value).toLocaleString(),
        flex: 1,
      },
      {
        field: 'duration',
        headerName: 'Took',
        type: 'number',
        valueGetter: ({ row }) =>
          new Date(row.completedAt) - new Date(row.startedAt),
        valueFormatter: ({ value }) =>
          prettyMs(value, { secondsDecimalDigits: 0 }),
        flex: 0.5,
      },
      {
        field: 'fileCount',
        headerName: 'Files',
        type: 'number',
        valueGetter: ({ row }) => row.files.length,
        flex: 0.5,
      },
      {
        field: 'fieldCount',
        headerName: 'Fields',
        type: 'number',
        valueGetter: ({ row }) => row.fields.length,
        flex: 0.5,
      },
      {
        field: 'size',
        headerName: 'Size',
        type: 'number',
        valueFormatter: ({ value }) => prettyBytes(value),
        flex: 0.5,
      },
    ];
  }
}
